import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NotificationBell } from './NotificationBell';
import { 
    Music, 
    Send, 
    Users, 
    LogOut, 
    Menu, 
    X,
    ChevronRight,
    Radio,
    Settings,
    RefreshCw,
    FolderOpen,
    UserPlus,
    Badge,
    Mail,
    BarChart2,
    Bell,
    CheckCircle,
    XCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Layout = ({ children }) => {
    const { user, logout, isAdmin, token } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [posterRequestCount, setPosterRequestCount] = useState(0);
    const [inviteCount, setInviteCount] = useState(0);
    const [notificationCount, setNotificationCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Fetch poster request count for admin badge
    useEffect(() => {
        if (isAdmin && token) {
            fetchPosterRequestCount();
        }
    }, [isAdmin, token]);

    // Fetch invite count for submitters
    useEffect(() => {
        if (user?.role === 'submitter' && token) {
            fetchInviteCount();
        }
    }, [user, token]);

    const fetchPosterRequestCount = async () => {
        try {
            const response = await fetch(`${API}/admin/poster-requests/count`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPosterRequestCount(data.count);
            }
        } catch (error) {
            // Silent fail
        }
    };

    const fetchInviteCount = async () => {
        try {
            const response = await fetch(`${API}/invites/count`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setInviteCount(data.count);
            }
        } catch (error) {
            // Silent fail
        }
    };

    const fetchNotificationCount = async () => {
        try {
            const response = await fetch(`${API}/notifications/count`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setNotificationCount(data.unread_count);
            }
        } catch (error) {
            // Silent fail
        }
    };

    const fetchNotifications = async () => {
        try {
            const response = await fetch(`${API}/notifications?limit=10`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setNotifications(data);
            }
        } catch (error) {
            // Silent fail
        }
    };

    const markNotificationRead = async (notificationId) => {
        try {
            await fetch(`${API}/notifications/${notificationId}/read`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchNotificationCount();
            fetchNotifications();
        } catch (error) {
            // Silent fail
        }
    };

    const markAllRead = async () => {
        try {
            await fetch(`${API}/notifications/read-all`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setNotificationCount(0);
            fetchNotifications();
        } catch (error) {
            // Silent fail
        }
    };

    // Fetch notification count for submitters
    useEffect(() => {
        if (user?.role === 'submitter' && token) {
            fetchNotificationCount();
            fetchNotifications();
        }
    }, [user, token]);

    const isPoster = user?.role === 'poster';

    // Build navigation items based on role
    const navItems = [];

    // Common items for artists/submitters
    if (!isPoster) {
        navItems.push({ 
            path: '/opportunities', 
            label: 'Sync Opportunities', 
            icon: Music 
        });
        navItems.push({ 
            path: '/submissions', 
            label: 'My Submissions', 
            icon: Send 
        });
        navItems.push({ 
            path: '/invites', 
            label: 'Invites', 
            icon: Mail,
            badge: inviteCount
        });
    }

    // Poster-specific items
    if (isPoster) {
        navItems.push({ 
            path: '/poster/dashboard', 
            label: 'My Projects', 
            icon: FolderOpen 
        });
        navItems.push({ 
            path: '/poster/invites', 
            label: 'Invite to Project', 
            icon: Send 
        });
        navItems.push({ 
            path: '/poster/team', 
            label: 'Team', 
            icon: Users 
        });
    }

    // Common for all
    navItems.push({ 
        path: '/settings', 
        label: 'Settings', 
        icon: Settings 
    });

    // Admin items
    if (isAdmin) {
        navItems.push({ 
            path: '/admin/analytics', 
            label: 'Analytics', 
            icon: BarChart2 
        });
        navItems.push({ 
            path: '/admin/users', 
            label: 'Manage Users', 
            icon: Users 
        });
        navItems.push({ 
            path: '/admin/poster-requests', 
            label: 'Poster Requests', 
            icon: UserPlus,
            badge: posterRequestCount
        });
        navItems.push({ 
            path: '/poster/dashboard', 
            label: 'Poster Projects', 
            icon: FolderOpen 
        });
        navItems.push({ 
            path: '/admin/scraper', 
            label: 'Scraper Settings', 
            icon: RefreshCw 
        });
    }

    const isActive = (path) => location.pathname.startsWith(path);

    return (
        <div className="min-h-screen bg-[#121212]">
            {/* Mobile menu button */}
            <div className="lg:hidden fixed top-4 left-4 z-50">
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-2 rounded-lg bg-[#262626] text-white hover:bg-[#333] transition-colors"
                >
                    {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Sidebar */}
            <AnimatePresence>
                {(sidebarOpen || typeof window !== 'undefined' && window.innerWidth >= 1024) && (
                    <>
                        {/* Mobile overlay */}
                        {sidebarOpen && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setSidebarOpen(false)}
                                className="lg:hidden fixed inset-0 bg-black/50 z-30"
                            />
                        )}

                        {/* Sidebar content */}
                        <motion.aside
                            initial={{ x: -280 }}
                            animate={{ x: 0 }}
                            exit={{ x: -280 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="fixed left-0 top-0 h-screen w-[280px] bg-[#1A1A1A] border-r border-[#333] z-40 lg:z-0 flex flex-col"
                        >
                            {/* Logo */}
                            <div className="p-6 border-b border-[#333]">
                                <Link to="/" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
                                    <div className="w-10 h-10 rounded-xl bg-[#F51E1E] flex items-center justify-center">
                                        <Radio size={22} className="text-white" />
                                    </div>
                                    <div>
                                        <h1 className="text-lg font-bold text-white">Pitch Room Sync</h1>
                                        <p className="text-xs text-[#888]">
                                            {isPoster ? 'Project Owner' : 'Artist Portal'}
                                        </p>
                                    </div>
                                </Link>
                            </div>

                            {/* Navigation */}
                            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                                {navItems.map((item) => {
                                    const Icon = item.icon;
                                    const active = isActive(item.path);
                                    
                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            onClick={() => setSidebarOpen(false)}
                                            className={`
                                                flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                                                ${active 
                                                    ? 'bg-[#F51E1E] text-white' 
                                                    : 'text-[#A3A3A3] hover:text-white hover:bg-[#262626]'
                                                }
                                            `}
                                        >
                                            <Icon size={20} />
                                            <span className="font-medium flex-1">{item.label}</span>
                                            {item.badge > 0 && (
                                                <span className={`
                                                    px-2 py-0.5 rounded-full text-xs font-bold
                                                    ${active ? 'bg-white text-[#F51E1E]' : 'bg-[#F51E1E] text-white'}
                                                `}>
                                                    {item.badge}
                                                </span>
                                            )}
                                            {active && <ChevronRight size={18} />}
                                        </Link>
                                    );
                                })}
                            </nav>

                            {/* User section */}
                            <div className="p-4 border-t border-[#333]">
                                {/* Notification bell for submitters */}
                                {user?.role === 'submitter' && (
                                    <div className="relative mb-3">
                                        <button
                                            onClick={() => {
                                                setShowNotifications(!showNotifications);
                                                if (!showNotifications) fetchNotifications();
                                            }}
                                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#262626] transition-all"
                                        >
                                            <Bell size={20} />
                                            <span className="font-medium flex-1">Notifications</span>
                                            {notificationCount > 0 && (
                                                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#F51E1E] text-white">
                                                    {notificationCount}
                                                </span>
                                            )}
                                        </button>
                                        
                                        {/* Notification dropdown */}
                                        <AnimatePresence>
                                            {showNotifications && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="absolute bottom-full left-0 right-0 mb-2 bg-[#262626] border border-[#404040] rounded-xl shadow-xl overflow-hidden max-h-[300px] overflow-y-auto z-50"
                                                >
                                                    <div className="p-3 border-b border-[#404040] flex items-center justify-between">
                                                        <span className="text-sm font-semibold text-white">Notifications</span>
                                                        {notificationCount > 0 && (
                                                            <button
                                                                onClick={markAllRead}
                                                                className="text-xs text-[#F51E1E] hover:underline"
                                                            >
                                                                Mark all read
                                                            </button>
                                                        )}
                                                    </div>
                                                    {notifications.length === 0 ? (
                                                        <div className="p-4 text-center text-[#666] text-sm">
                                                            No notifications yet
                                                        </div>
                                                    ) : (
                                                        notifications.map((notif) => (
                                                            <div
                                                                key={notif.id}
                                                                onClick={() => {
                                                                    if (!notif.read) markNotificationRead(notif.id);
                                                                    if (notif.link) navigate(notif.link);
                                                                    setShowNotifications(false);
                                                                }}
                                                                className={`p-3 border-b border-[#333] cursor-pointer hover:bg-[#333] transition-colors ${!notif.read ? 'bg-[#1A1A1A]' : ''}`}
                                                            >
                                                                <div className="flex items-start gap-2">
                                                                    {notif.type === 'submission_accepted' ? (
                                                                        <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                                                                    ) : notif.type === 'submission_rejected' ? (
                                                                        <XCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                                                                    ) : (
                                                                        <Bell size={16} className="text-[#666] mt-0.5 flex-shrink-0" />
                                                                    )}
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className={`text-sm font-medium ${!notif.read ? 'text-white' : 'text-[#A3A3A3]'}`}>
                                                                            {notif.title}
                                                                        </p>
                                                                        <p className="text-xs text-[#666] truncate">{notif.message}</p>
                                                                        <p className="text-xs text-[#555] mt-1">
                                                                            {new Date(notif.created_at).toLocaleDateString()}
                                                                        </p>
                                                                    </div>
                                                                    {!notif.read && (
                                                                        <span className="w-2 h-2 rounded-full bg-[#F51E1E] flex-shrink-0" />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}
                                
                                <div className="flex items-center gap-3 px-4 py-3">
                                    <div className="w-10 h-10 rounded-full bg-[#262626] flex items-center justify-center">
                                        <span className="text-sm font-bold text-white">
                                            {user?.first_name?.[0]}{user?.last_name?.[0]}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-white truncate">
                                            {user?.first_name} {user?.last_name}
                                        </p>
                                        <p className="text-xs text-[#888] truncate capitalize">
                                            {user?.role}
                                        </p>
                                    </div>
                                    <NotificationBell />
                                    <button
                                        onClick={handleLogout}
                                        className="p-2 rounded-lg text-[#888] hover:text-white hover:bg-[#262626] transition-colors"
                                        title="Logout"
                                    >
                                        <LogOut size={18} />
                                    </button>
                                </div>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>

            {/* Desktop sidebar spacer */}
            <div className="hidden lg:block w-[280px] flex-shrink-0" />

            {/* Main content */}
            <main className="lg:ml-[280px] min-h-screen">
                <div className="p-6 lg:p-8 pt-16 lg:pt-8">
                    {children}
                </div>
            </main>
        </div>
    );
};
